// eslint-disable-next-line import/no-cycle
import { PatientInviteErrors, PatientObj } from 'pages/Dashboard/types/patient.types';
import { ListPracticesAPIResponse } from 'types/Entitites';
import { GoogleDate, Time } from 'utils/dateUtils';
import { GooglePhoneNumber } from 'utils/phoneUtils';

export enum emotions {
  angry,
  elated,
  happy,
  humiliated,
  sad,
}

export enum activityTypes {
  cycle,
  sleep,
  swim,
  tennis,
  walk,
}

export enum lifeEventTypes {
  professional,
  personal,
}

export enum PatientRecordRetrievalStatus {
  PATIENT_RECORD_RETRIEVAL_STATUS_UNSPECIFIED = 'PATIENT_RECORD_RETRIEVAL_STATUS_UNSPECIFIED',
  PATIENT_RECORD_RETRIEVAL_STATUS_PENDING = 'PATIENT_RECORD_RETRIEVAL_STATUS_PENDING',
  PATIENT_RECORD_RETRIEVAL_STATUS_FETCHING = 'PATIENT_RECORD_RETRIEVAL_STATUS_FETCHING',
  PATIENT_RECORD_RETRIEVAL_STATUS_FETCHED = 'PATIENT_RECORD_RETRIEVAL_STATUS_FETCHED',
  PATIENT_RECORD_RETRIEVAL_STATUS_TRANSFORMING = 'PATIENT_RECORD_RETRIEVAL_STATUS_TRANSFORMING',
  PATIENT_RECORD_RETRIEVAL_STATUS_TRANSFORMED = 'PATIENT_RECORD_RETRIEVAL_STATUS_TRANSFORMED',
  PATIENT_RECORD_RETRIEVAL_STATUS_RETRIEVED = 'PATIENT_RECORD_RETRIEVAL_STATUS_RETRIEVED',
  PATIENT_RECORD_RETRIEVAL_STATUS_FAILED = 'PATIENT_RECORD_RETRIEVAL_STATUS_FAILED',
}

export interface StringBooleanIndex {
  [k: string]: boolean;
}

type WaypointPair = {
  id: string;
  name: string;
  displayName?: string;
};

export interface WayPointsIndex {
  [k: string]: Array<WaypointPair>;
}

export type Waypoints = {
  name: string;
  id: string;
  displayName?: string;
  category: string;
  creatorType?: string;
  parentWaypoint?: Waypoints;
  type?: string;
  description?: string;
};

export type CustomWaypoints = {
  name: string;
  id?: string;
  displayName?: string;
  category: string;
  creatorType?: string;
  parentWaypoint?: Waypoints;
};

export enum UserMetadataFlags {
  AddPatientPreference = 'add-patient-preference',
  SuggestScaleFeatureFlag = 'suggest_scale_feature_flag',
  NumberOfRowsPerPage = 'number_of_rows_per_page',
  DefaultSortField = 'default_sort_field',
  DefaultSortDirection = 'default_sort_direction',
  EnableNewList = 'enable_new_list',
  NewVersionFeatureFlag = 'new_version_feature_flag',
  EnableNewPatientDetailsView = 'enable_new_patient_details_view',
  ShowAllScalesForBillingAdmin = 'ShowAllScalesForBillingAdmin',
  DisableNewUI = 'new_ui_disabled',
  EnableSessionRecording = 'enable_notetaker',
  EnableFastAPI = 'enable_fast_api',
  EnableHrRefresh = 'enable_hr_refresh',
  AddPatientWithoutInvite = 'add_patient_without_invite',
  UserType = 'user-type',
  IsOnboardedFlag = 'is_onboarded_flag',
}

export enum AddPatientPreference {
  Invite = 'invite',
  Demographic = 'demographic'
}

export type UserMetadata = {
  userId?: string;
  flag: {
    name: UserMetadataFlags;
    value?: string;
  };
};

export type UserMetadataGetPayload = {
  name: UserMetadataFlags;
  userId: string;
}

export type PatientFormData = {
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  email?: string;
  dob?: string;
  gender?: string;
  mobile?: string;
  street?: string;
  streetAdditional?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  nextAppointmentDate?: string;
  id?: string;
  practiceId?: string;
  providerId?: string;
  consent?: boolean;
};

export type AddressObj = {
  city?: string;
  country?: string;
  state?: string;
  street?: string;
  streetAdditional?: string;
  zip?: string;
};

export interface PatientHealthRecordStatusAPIResponse {
  status: {
    recordRetrievalStatus: PatientRecordRetrievalStatus;
  };
}

export type ReducerState = {
  formData: PatientFormData;
  waypoints: Waypoints[];
  patientList: PatientObj[]
  patientInviteErrorsMap: PatientInviteErrors;
};

export type PatientContextValues = {
  state: ReducerState;
  dispatch: React.Dispatch<PatientAction>;
  addingPatient: boolean;
  practicesList: ListPracticesAPIResponse | null;
};

export interface PatientInvite {
  mobile: string;
  email?: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  middleName?: string;
}

export type InvitePatientFormData = Required<
  Pick<PatientForm, 'practiceId' | 'providerId'>
> & {
  patients: PatientInvite[];
};

export type CreatePatientFormData = Required<Omit<PatientForm, 'patients'>>;

export interface PatientForm {
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  email?: string;
  dob?: string;
  gender?: string;
  mobile?: string;
  street?: string;
  streetAdditional?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  nextAppointmentDate?: string;
  practiceId?: string;
  providerId?: string;
  patients?: PatientInvite[];
  consent?: boolean;
  alias?: string;
}

interface Appointment {
  date: GoogleDate;
  time: Time;
}

interface PatientAddress {
  city?: string;
  country?: string;
  state?: string;
  street?: string;
  streetAdditional?: string;
  zip?: string;
}

interface PatientName {
  firstName: string;
  lastName: string;
  middleName: string;
  preferredName: string;
}

export interface PatientPayload {
  address: PatientAddress;
  dateOfBirth: GoogleDate;
  email: string;
  gender: string;
  name: PatientName;
  phoneNumber: GooglePhoneNumber;
  consent?: boolean;
}

export interface PatientDemographicInfoPayload {
  appointment: Appointment;
  patient: PatientPayload;
  practiceId: string;
  headlampProviderId: string;
  createWithoutInvitation: boolean;
}

export interface PatientInvitesPayload {
  patient: Pick<PatientPayload, 'email' | 'phoneNumber' | 'name'>[];
  practiceId: string;
  headlampProviderId: string;
}

export enum PatientContextAction {
  SET_FORM_DATA = 'SET_FORM_DATA',
  SET_WAYPOINTS = 'SET_WAYPOINTS',
  RESET_FORM_DATA = 'RESET_FORM_DATA',
  SET_PATIENT_LIST = 'SET_PATIENT_LIST',
  SET_PATIENT_INVITE_ERROR_LIST = 'SET_PATIENT_INVITE_ERROR_LIST',
}

export type PatientActionPayload = {
  formData?: PatientFormData;
  waypoints?: Waypoints[];
  patientList?: PatientObj[];
  inviteErrors?: PatientInviteErrors;
};

export type PatientAction = {
  type: PatientContextAction;
  payload?: PatientActionPayload;
};

export enum PatientGender {
  MALE = 'PATIENT_GENDER_MALE',
  FEMALE = 'PATIENT_GENDER_FEMALE',
}

export enum SourceType {
  SOURCE_TYPE_UNSPECIFIED = 0,
  SOURCE_TYPE_HEALTH_GORILLA = 1,
  SOURCE_TYPE_PATIENT_REPORTED = 2,
  SOURCE_TYPE_PROVIDER_REPORTED = 3,
}

export const SourceTypeMap = {
  SOURCE_TYPE_UNSPECIFIED: 'SOURCE_TYPE_UNSPECIFIED',
  SOURCE_TYPE_HEALTH_GORILLA: 'SOURCE_TYPE_HEALTH_GORILLA',
  SOURCE_TYPE_PATIENT_REPORTED: 'SOURCE_TYPE_PATIENT_REPORTED',
  SOURCE_TYPE_PROVIDER_REPORTED: 'SOURCE_TYPE_PROVIDER_REPORTED',

};

export type PatientFilters = { filters: string[] };

type ResendInvitePatient = {
  id: string;
  email: string;
  phoneNumber: GooglePhoneNumber | undefined;
}

export type ResendInvitePayload = {
  patient: ResendInvitePatient[],
  headlampProviderId: string;
  practiceId: string;
}

export type ResolveInvitePayload = {
  patient: ResendInvitePatient[],
  headlampProviderId: string;
  practiceId: string;
}

export type ProviderConsent = { date: string };

export type CreateWaypointPayload = {
  waypoint: Waypoints;
}

export type GenerateNotesFromTranscriptPayload = {
  transcript: string,
  templateId: string,
  sessionId: string,
  patientId: string,
  sessionDurationSec: number;
}

export type GetDestinationUrlsResponse = {
  audio: string;
  transcript: string;
  sessionId: string;
}

export type SendTranscriptFeedbackPayload = {
  upvote: string;
  feedbackItems: string[];
  feedback: string;
}

export type EditTranscriptPayload = {
  sessionNotes: string;
}

export type Session = {
  sessionId: string;
  sessionName: string;
  sentimentAnalysis: Record<string, string>;
  keywords: string[];
  transcript: string;
  notes: string;
  templateId: string;
  includeHlContext: boolean;
  formatId: string;
  sessionDuration: string;
  feedback: {
    upvote: string;
    feedbackBucket: string[];
  };
};

export type GetSessionListResponse = {
  sessions: Session[];
  pagination: {
    offset: number;
    limit: number;
    total: string;
  };
}

export type GetMetadataResponse = {
  templates: {
    id: string;
    display: string;
  }[];
  formats: string[];
  feedbackBuckets: {
    key: string;
    displayName: string;
  }[];
};

export type GetSessionAuthTokenResponse = {
  token: string;
  url: string;
}

export enum Status {
  DEFAULT,
  RECORDING,
  PAUSED,
  ERROR,
}

export type GetPDFUrlResponse = {
  url: string;
}
